/**
 * 🎨 PROFILE HEADERS - Arrière-plans personnalisables pour profil Heartbeats
 * 
 * Comme les cartes de fidélité, mais pour ton profil !
 * Personnalise l'en-tête derrière ta photo 💜
 */

import React from 'react';
import { motion } from 'framer-motion';
import { Check } from 'lucide-react';

// 🎨 CATALOGUE DES HEADERS
export const PROFILE_HEADERS = [
  // 🌈 DÉGRADÉS (Gratuits)
  {
    id: 'gradient_purple_pink',
    name: 'Violet & Rose',
    emoji: '💜',
    category: 'gradient',
    premium: false,
    style: 'linear-gradient(135deg, #a855f7 0%, #ec4899 50%, #f43f5e 100%)'
  },
  {
    id: 'gradient_sunset',
    name: 'Coucher de soleil',
    emoji: '🌅',
    category: 'gradient',
    premium: false,
    style: 'linear-gradient(135deg, #f97316 0%, #f43f5e 50%, #a855f7 100%)'
  },
  {
    id: 'gradient_ocean',
    name: 'Océan',
    emoji: '🌊',
    category: 'gradient',
    premium: false,
    style: 'linear-gradient(135deg, #06b6d4 0%, #3b82f6 50%, #8b5cf6 100%)'
  },
  {
    id: 'gradient_forest',
    name: 'Forêt',
    emoji: '🌲',
    category: 'gradient',
    premium: false,
    style: 'linear-gradient(135deg, #10b981 0%, #059669 50%, #065f46 100%)'
  },
  {
    id: 'gradient_fire',
    name: 'Feu ardent',
    emoji: '🔥',
    category: 'gradient',
    premium: false,
    style: 'linear-gradient(135deg, #f59e0b 0%, #ef4444 50%, #dc2626 100%)'
  },
  {
    id: 'gradient_night',
    name: 'Nuit étoilée',
    emoji: '🌙',
    category: 'gradient',
    premium: false,
    style: 'linear-gradient(135deg, #1e1b4b 0%, #312e81 50%, #4338ca 100%)'
  },
  
  // 💎 PREMIUM
  {
    id: 'gradient_gold',
    name: 'Or Royal',
    emoji: '👑',
    category: 'premium',
    premium: true,
    style: 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 30%, #d97706 70%, #fbbf24 100%)'
  },
  {
    id: 'gradient_diamond',
    name: 'Diamant',
    emoji: '💎',
    category: 'premium',
    premium: true,
    style: 'linear-gradient(135deg, #e0f2fe 0%, #7dd3fc 30%, #38bdf8 70%, #0ea5e9 100%)'
  },
  {
    id: 'gradient_aurora',
    name: 'Aurore boréale',
    emoji: '✨',
    category: 'premium',
    premium: true,
    style: 'linear-gradient(135deg, #4ade80 0%, #22d3ee 25%, #a78bfa 50%, #f472b6 75%, #fb7185 100%)'
  },
  {
    id: 'gradient_rainbow',
    name: 'Arc-en-ciel',
    emoji: '🌈',
    category: 'premium',
    premium: true,
    style: 'linear-gradient(135deg, #ef4444 0%, #f97316 17%, #eab308 33%, #22c55e 50%, #3b82f6 67%, #8b5cf6 83%, #ec4899 100%)'
  },
  {
    id: 'gradient_galaxy',
    name: 'Galaxie',
    emoji: '🌌',
    category: 'premium',
    premium: true,
    style: 'linear-gradient(135deg, #0f0c29 0%, #302b63 50%, #24243e 100%)'
  },
];

// 🖼️ Composant Header personnalisé
export const ProfileHeader = ({ 
  headerId = 'gradient_purple_pink',
  customUrl = null,
  height = 80,
  children
}) => {
  const header = PROFILE_HEADERS.find(h => h.id === headerId) || PROFILE_HEADERS[0];
  
  return (
    <div 
      className="w-full relative overflow-hidden"
      style={{ 
        height: `${height}px`,
        background: customUrl ? `url(${customUrl})` : header.style,
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }}
    >
      {children}
    </div>
  );
};

// 🎨 Sélecteur de Headers
export const HeaderSelector = ({ 
  currentHeader = 'gradient_purple_pink',
  onSelect,
  isDarkMode = true,
  userHasPremium = false,
  showPremiumLock = true,
  compact = false
}) => {
  const freeHeaders = PROFILE_HEADERS.filter(h => !h.premium);
  const premiumHeaders = PROFILE_HEADERS.filter(h => h.premium);
  
  const HeaderButton = ({ header, isSelected }) => {
    const isLocked = header.premium && !userHasPremium && showPremiumLock;
    
    return (
      <motion.button
        key={header.id}
        onClick={() => !isLocked && onSelect(header.id)}
        disabled={isLocked}
        className={`
          relative rounded-lg overflow-hidden transition-all
          ${compact ? 'w-12 h-8' : 'w-16 h-10'}
          ${isSelected ? 'ring-2 ring-white ring-offset-2 ring-offset-gray-900 scale-105' : ''}
          ${isLocked ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:scale-105'}
        `}
        style={{ background: header.style }}
        whileHover={{ scale: isLocked ? 1 : 1.05 }}
        whileTap={{ scale: isLocked ? 1 : 0.95 }}
        title={header.name}
      >
        {/* Emoji */}
        <span className={`absolute inset-0 flex items-center justify-center ${compact ? 'text-xs' : 'text-sm'}`}>
          {header.emoji}
        </span>
        
        {/* Selected indicator */}
        {isSelected && (
          <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
            <Check className={`${compact ? 'w-3 h-3' : 'w-4 h-4'} text-white`} />
          </div>
        )}
        
        {/* Premium lock */}
        {isLocked && (
          <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
            <span className={compact ? 'text-xs' : 'text-sm'}>🔒</span>
          </div>
        )}
      </motion.button>
    );
  };
  
  if (compact) {
    return (
      <div className="flex flex-wrap gap-1.5">
        {PROFILE_HEADERS.map(header => (
          <HeaderButton 
            key={header.id}
            header={header}
            isSelected={currentHeader === header.id}
          />
        ))}
      </div>
    );
  }
  
  return (
    <div className="space-y-3">
      {/* Headers gratuits */}
      <div>
        <p className={`text-xs mb-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          🎨 Dégradés
        </p>
        <div className="flex flex-wrap gap-2">
          {freeHeaders.map(header => (
            <HeaderButton 
              key={header.id}
              header={header}
              isSelected={currentHeader === header.id}
            />
          ))}
        </div>
      </div>
      
      {/* Headers premium */}
      <div>
        <p className={`text-xs mb-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          💎 Premium
        </p>
        <div className="flex flex-wrap gap-2">
          {premiumHeaders.map(header => (
            <HeaderButton 
              key={header.id}
              header={header}
              isSelected={currentHeader === header.id}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default { ProfileHeader, HeaderSelector, PROFILE_HEADERS };
